import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

const DARK_MODE_STORAGE_KEY = 'psod_pref_dark_mode';
const SHOW_BS_DATE_STORAGE_KEY = 'psod_pref_show_bs_date';

interface DateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

type DateInput = string | Date | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class AppPreferencesService {
  private readonly darkModeState = signal(false);
  private readonly showBsDateState = signal(false);
  private readonly datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  private readonly timePattern = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  get darkMode(): boolean {
    return this.darkModeState();
  }

  get showBsDate(): boolean {
    return this.showBsDateState();
  }

  initializePreferences(): void {
    if (!this.isBrowser()) {
      return;
    }

    this.darkModeState.set(this.readBooleanPreference(DARK_MODE_STORAGE_KEY));
    this.showBsDateState.set(this.readBooleanPreference(SHOW_BS_DATE_STORAGE_KEY));
    this.applyThemeClass();
  }

  setDarkMode(enabled: boolean): void {
    this.darkModeState.set(enabled);
    this.writeBooleanPreference(DARK_MODE_STORAGE_KEY, enabled);
    this.applyThemeClass();
  }

  setShowBsDate(enabled: boolean): void {
    this.showBsDateState.set(enabled);
    this.writeBooleanPreference(SHOW_BS_DATE_STORAGE_KEY, enabled);
    this.dispatchPreferenceChange('showBsDate', enabled);
  }

  formatDateOnly(value: DateInput): string {
    const parsed = this.parseDateTime(value);
    if (!parsed) {
      return '-';
    }

    return this.formatDateParts(parsed);
  }

  formatDateTime(value: DateInput): string {
    const parsed = this.parseDateTime(value);
    if (!parsed) {
      return '-';
    }

    return this.formatDateTimeParts(parsed);
  }

  formatDateByPreference(value: DateInput): string {
    return this.formatDateOnly(value);
  }

  formatDateTimeByPreference(value: DateInput): string {
    return this.formatDateTime(value);
  }

  formatCurrentTimeLabel(value: DateInput): string {
    const parsed = this.parseDateTime(value);
    if (!parsed) {
      return '';
    }

    return this.formatDateTimeParts(parsed);
  }

  private parseDateTime(value: DateInput): DateTimeParts | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }

      return {
        year: value.getFullYear(),
        month: value.getMonth() + 1,
        day: value.getDate(),
        hour: value.getHours(),
        minute: value.getMinutes(),
        second: value.getSeconds(),
      };
    }

    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const [datePart, timePart] = normalized.split(/[T\s]/, 2);
    const dateMatch = this.datePattern.exec(datePart);
    if (!dateMatch) {
      const fallback = new Date(normalized.replace(' ', 'T'));
      if (Number.isNaN(fallback.getTime())) {
        return null;
      }

      return {
        year: fallback.getFullYear(),
        month: fallback.getMonth() + 1,
        day: fallback.getDate(),
        hour: fallback.getHours(),
        minute: fallback.getMinutes(),
        second: fallback.getSeconds(),
      };
    }

    const timeMatch = timePart ? this.timePattern.exec(timePart) : null;
    const year = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const day = Number(dateMatch[3]);
    const hour = Number(timeMatch?.[1] ?? 0);
    const minute = Number(timeMatch?.[2] ?? 0);
    const second = Number(timeMatch?.[3] ?? 0);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second)
    ) {
      return null;
    }

    return { year, month, day, hour, minute, second };
  }

  private formatDateParts(parts: DateTimeParts): string {
    return `${parts.day.toString().padStart(2, '0')}-${parts.month.toString().padStart(2, '0')}-${parts.year}`;
  }

  private formatDateTimeParts(parts: DateTimeParts): string {
    return `${this.formatDateParts(parts)} ${parts.hour.toString().padStart(2, '0')}:${parts.minute.toString().padStart(2, '0')}:${parts.second.toString().padStart(2, '0')}`;
  }

  private readBooleanPreference(key: string): boolean {
    const raw = this.document.defaultView?.localStorage.getItem(key);
    return raw === 'true';
  }

  private writeBooleanPreference(key: string, value: boolean): void {
    if (!this.isBrowser()) {
      return;
    }

    this.document.defaultView?.localStorage.setItem(key, String(value));
  }

  private dispatchPreferenceChange(name: string, value: boolean): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    win.dispatchEvent(
      new CustomEvent('app-preferences-change', {
        detail: {
          name,
          value,
        },
      }),
    );
  }

  private applyThemeClass(): void {
    if (!this.isBrowser()) {
      return;
    }

    const root = this.document.documentElement;
    const body = this.document.body;
    root.classList.toggle('app-theme-dark', this.darkModeState());
    body.classList.toggle('app-theme-dark', this.darkModeState());
    root.style.colorScheme = this.darkModeState() ? 'dark' : 'light';
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
